import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
import { useDebouncedCallback } from "use-debounce";
//#endregion
//#region types
import { MultiSearchInputProps } from "models/propTypes/wintFormPropsTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import { CommonConstants, FieldConstants, IconDefinitionConstants } from "utils/constants";
import { DialogEnums } from "models/enums";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
//#endregion
//#region components
import Icon from "../../icon";
import {
  Grid,
  CircularProgress,
  Autocomplete,
  Checkbox,
  Chip,
  TextField,
  AutocompleteChangeReason,
  InputAdornment,
  AutocompleteChangeDetails,
} from "@mui/material";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

const MultiSearchInput: React.FC<MultiSearchInputProps> = (props) => {
  const selectedItems = props.value ?? [];
  const [inputValue, setInputValue] = React.useState<string>("");

  const [options, setOptions] = React.useState<AutocompleteListItem[]>();
  const [currentAction, setCurrentAction] = React.useState<AutocompleteListItem>();

  const [loading, setLoading] = React.useState<boolean>(props.isLoading ?? false);

  const itemNone: AutocompleteListItem = {
    value: UtilsHelper.getSelectionNoneText(),
  };

  const itemSelectAll: AutocompleteListItem = {
    id: CommonConstants.SelectAllSelectionId,
    value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.All),
  };

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const triggerOnChange = (newValue: AutocompleteListItem[]) => {
    if (props.onChange) {
      const syntheticEvent = {
        target: { value: newValue },
      } as React.ChangeEvent<any>;
      props.onChange(syntheticEvent);
    }
  };

  const getSuggestedItems = (): AutocompleteListItem[] => {
    const suggestedItems = props.includeNone ? [itemNone, ...(props.suggestedItems ?? [])] : props.suggestedItems;
    return (
      suggestedItems?.map((x) => ({
        ...x,
        group: x.group ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
      })) ?? []
    );
  };

  const setDefaultOptions = () => {
    searchItems("").then((items) => {
      if (
        JSON.stringify(
          (items ?? []).map((a) => {
            return { id: a.id, val: a.value };
          })
        ) !==
        JSON.stringify(
          (options ?? []).map((a) => {
            return { id: a.id, val: a.value };
          })
        )
      )
        setOptions(items);
    });
  };

  const searchItems = async (value: string) => {
    let searchItems: AutocompleteListItem[] = [];

    if (value === "" && props.suggestedItems) {
      searchItems = getSuggestedItems();
    } else {
      if (props.items)
        if (typeof props.items === "function") {
          searchItems = await props.items(value, props.format ? UtilsHelper.applyMask(value, props.format) : undefined);
        } else {
          const items = props.includeNone ? [itemNone, ...(props.items ?? [])] : (props.items ?? []);
          searchItems = value
            ? items
                .map((x) => ({
                  index: `${x.value} ${x.description ?? ""}`.toLowerCase().indexOf(value.toLowerCase()),
                  item: x,
                }))
                .filter((x) => x.index >= 0)
                .sort((a, b) => a.index - b.index)
                .map((x) => x.item)
            : items;
        }
    }
    return searchItems;
  };

  const isActionOption = (option: AutocompleteListItem) => !!(option.onActionClicked || option.form);

  const getOptionKey = (option: AutocompleteListItem) => (option.id ?? option.value)?.toString();

  const getBaseOptions = (): AutocompleteListItem[] => options ?? getSuggestedItems();

  const getSelectableVisibleOptions = (): AutocompleteListItem[] => {
    const baseOptions = getBaseOptions();
    const filtered = baseOptions.filter(
      (option) =>
        (!props.error || !option.hideWhenInvalid) &&
        (!option.hide || !option.hide(baseOptions, inputValue)) &&
        !isActionOption(option) &&
        option.value !== UtilsHelper.getSelectionNoneText() &&
        !option.disabled
    );
    return filtered;
  };

  const areAllVisibleSelected = (): boolean => {
    const visible = getSelectableVisibleOptions();
    if (!visible.length) return false;
    const selectedKeys = new Set((selectedItems ?? []).map(getOptionKey));
    return visible.every((o) => selectedKeys.has(getOptionKey(o)));
  };

  const areSomeVisibleSelected = (): boolean => {
    const visible = getSelectableVisibleOptions();
    if (!visible.length) return false;
    const selectedKeys = new Set((selectedItems ?? []).map(getOptionKey));
    const selectedCount = visible.filter((o) => selectedKeys.has(getOptionKey(o))).length;
    return selectedCount > 0 && selectedCount < visible.length;
  };

  const handleSelectAllToggle = () => {
    const visible = getSelectableVisibleOptions();
    if (!visible.length) return;

    const visibleKeys = new Set(visible.map(getOptionKey));
    const current = selectedItems ?? [];

    let newSelection: AutocompleteListItem[];
    if (areAllVisibleSelected()) {
      newSelection = current.filter((x) => x.locked || !visibleKeys.has(getOptionKey(x)));
    } else {
      const mergedMap = new Map<string, AutocompleteListItem>();
      [...current, ...visible].forEach((o) => mergedMap.set(getOptionKey(o), o));
      newSelection = Array.from(mergedMap.values());
    }

    triggerOnChange(newSelection);
    props.setValue(newSelection, true);
    props.setTouched(true);
  };

  // Trim so a stray leading/trailing space doesn't make an otherwise-valid email invalid.
  const createFreeSoloItem = (value: string): AutocompleteListItem => {
    const trimmedValue = value.trim();
    return {
      id: trimmedValue,
      value: trimmedValue,
      description: "",
      group: "",
      data: "",
    };
  };

  const onChange = (
    event: any,
    newValue: (AutocompleteListItem | string)[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<AutocompleteListItem>
  ) => {
    setLoading(false);

    const selectAllItem = newValue.find(
      (i) => typeof i !== "string" && i.id === CommonConstants.SelectAllSelectionId
    ) as AutocompleteListItem | undefined;

    if (selectAllItem) {
      handleSelectAllToggle();
      return;
    }

    if (details?.option?.locked) {
      return;
    }

    const formattedValue = props.freeSolo
      ? // Whitespace-only free text trims down to nothing -- drop it instead of adding an invisible blank chip.
        newValue.flatMap((i) => {
          if (typeof i !== "string") return [i];
          const freeSoloItem = createFreeSoloItem(i);
          return UtilsHelper.isEmpty(freeSoloItem.value) ? [] : [freeSoloItem];
        })
      : (newValue?.filter((i) => typeof i !== "string") as AutocompleteListItem[]);

    triggerOnChange(formattedValue);
    props.setValue(formattedValue, true);
    props.setTouched(true, false);
  };

  const debouncedInputChange = useDebouncedCallback((value: string, reason: string) => {
    searchItems(value)
      .then((items) => setOptions(items))
      .finally(() => setLoading(false));
  }, FieldConstants.DebounceTime);

  const onInputChange = (event: object, value: string, reason: string) => {
    setLoading(true);

    switch (reason) {
      case "input":
        setInputValue(value);
        debouncedInputChange(value, reason);
        break;
      case "clear":
        setInputValue("");
        break;
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && props.freeSolo && !UtilsHelper.isEmpty(inputValue.trim())) {
      const newValues = [...selectedItems, createFreeSoloItem(inputValue)];
      props.setValue(newValues, true);
      props.setTouched(true, false);
      setInputValue("");
    }
  };

  const onOpen = () => {
    setDefaultOptions();
  };

  const onClose = () => {
    if (props.freeSolo && !UtilsHelper.isEmpty(inputValue.trim())) {
      const newValues = [...selectedItems, createFreeSoloItem(inputValue)];
      props.setValue(newValues, true);
      props.setTouched(true, false);
    }

    setInputValue("");
    props.onClose && props.onClose();
  };

  const onActionClicked = (option: AutocompleteListItem, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    setLoading(true);
    setCurrentAction(option);

    if (option.form) {
      dispatch(
        displayDialog({
          isOpen: false,
          id: option.title ?? "autoCompleteActionDialog",
          title: option.title ?? "",
          subTitle: option.subtitle ?? "",
          titleIcon: option.formIcon,
          dialogType: DialogEnums.DialogType.Content,
          actions: [
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveChanges),
            },
          ],
          children: option.form,
          onSubmit: (value: any) => {
            setLoading(false);
            setCurrentAction(undefined);
            props.setValue(value as AutocompleteListItem[], true);
          },
          onClose: () => {
            setCurrentAction(undefined);
            setLoading(false);
          },
          maxWidth: "sm",
        })
      );
    } else {
      option.onActionClicked &&
        option.onActionClicked(inputValue.toString()).then((res) => {
          setLoading(false);
          if (res) {
            option.onActionSuccess && option.onActionSuccess(res);
            if (option.actionSuccessMessage) {
              setTimeout(() => {
                setActionResult(res);
              }, 500);
            } else {
              setActionResult(res);
            }
          }
        });
    }
  };

  const setActionResult = (result: AutocompleteListItem) => {
    setCurrentAction(undefined);
    props.setValue([...selectedItems, result], true);
    props.setTouched(true);
  };

  const handleRemove = (item: AutocompleteListItem) => {
    const newSelection = selectedItems?.filter((x) => x.id !== item.id) ?? [];
    triggerOnChange(newSelection);
    props.setValue(newSelection, true);
    props.setTouched(true);
  };

  // Chips are removed by position: duplicate free-solo chips share the same id (the typed value),
  // so an id-based filter would delete every copy instead of just the clicked chip.
  const handleRemoveAt = (index: number) => {
    const newSelection = selectedItems?.filter((_, i) => i !== index) ?? [];
    triggerOnChange(newSelection);
    props.setValue(newSelection, true);
    props.setTouched(true);
  };

  const onOptionSelect = (item: AutocompleteListItem, selected: boolean, event: React.MouseEvent<HTMLDivElement>) => {
    if (item.id === CommonConstants.SelectAllSelectionId) {
      event.stopPropagation();
      handleSelectAllToggle();
      setInputValue("");
      return;
    }

    if (selected) {
      event.stopPropagation();
      if (item.locked) {
        return;
      }
      handleRemove(item);
    } else if (props.forceSingleSelection) {
      event.stopPropagation();
      props.setValue([item], true);
      props.setTouched(true);
    }

    setInputValue("");
  };

  return (
    <Autocomplete
      multiple
      freeSolo={props.freeSolo}
      id={`multipleAutoComplete-${props.name}`}
      options={(props.includeSelectAll && !props.forceSingleSelection ? [itemSelectAll] : [])
        .concat(getBaseOptions())
        .concat(props.actions ?? [])}
      getOptionLabel={(option) => (typeof option !== "string" ? `${option.value} ${option.description}` : "")}
      value={selectedItems}
      renderOption={(htmlProps, option) => {
        const matches = match(option.value, inputValue.toString());
        const parts = parse(option.value, matches);
        const selectedAction = currentAction && currentAction?.id === option.id;
        const selected = selectedItems?.find((s) => option.id === s.id) !== undefined;

        return (
          <li {...htmlProps} key={option.id}>
            {option.id === CommonConstants.SelectAllSelectionId ? (
              <Grid container onClick={onOptionSelect.bind(null, option, selected)} className={option.className}>
                <Grid container wrap="nowrap">
                  <Checkbox
                    checked={areAllVisibleSelected()}
                    indeterminate={areSomeVisibleSelected() && !areAllVisibleSelected()}
                    className={clsx(cssExports.checkBoxOption)}
                  />
                  {props.selectAllIcon ? (
                    <span className={cssExports.icon}>
                      <Icon icon={props.selectAllIcon} />
                    </span>
                  ) : (
                    <></>
                  )}
                  <span className={clsx(cssExports.optionTextContainer, cssExports.hasCheckBox)}>
                    <span className={cssExports.optionText}>
                      {parts.map((part, index) => (
                        <span key={index} className={clsx(part.highlight ? cssExports.highlightText : "")}>
                          {part.text}
                        </span>
                      ))}
                    </span>
                  </span>
                </Grid>
              </Grid>
            ) : option.onActionClicked || option.form ? (
              <Grid
                onClick={onActionClicked.bind(null, option)}
                className={clsx(cssExports.actionItemContainer, option.className)}
              >
                <Grid container className={clsx(cssExports.actionTitleOuter)}>
                  <Grid item>
                    {selectedAction && loading ? (
                      <CircularProgress size={20} className={cssExports.buttonProgressWhite} />
                    ) : selectedAction ? (
                      <span className={clsx(cssExports.actionIcon, cssExports.success)}>
                        <Icon icon={IconDefinitionConstants.WCheck} />
                      </span>
                    ) : option.icon ? (
                      <span className={cssExports.actionIcon}>
                        <Icon icon={option.icon} />
                      </span>
                    ) : (
                      <></>
                    )}
                  </Grid>
                  <Grid item xs>
                    <Grid item className={cssExports.actionTitle}>
                      {currentAction?.actionSuccessMessage && selectedAction && !loading
                        ? currentAction.actionSuccessMessage
                        : option.value.replace(FieldConstants.ValueTemplateText, inputValue.toString() ?? "")}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <Grid container className={option.className} onClick={onOptionSelect.bind(null, option, selected)}>
                <Grid container wrap="nowrap">
                  <Checkbox checked={selected} className={cssExports.checkBoxOption} />
                  {option.icon ? (
                    <span className={cssExports.icon}>
                      <Icon icon={option.icon} />
                    </span>
                  ) : (
                    <></>
                  )}
                  <span
                    className={clsx(
                      cssExports.optionTextContainer,
                      option.icon ? cssExports.hasIcon : "",
                      cssExports.hasCheckBox
                    )}
                  >
                    <span className={cssExports.optionText}>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          className={clsx(
                            part.highlight ? cssExports.highlightText : "",
                            option.description ? cssExports.withSubText : ""
                          )}
                        >
                          {part.text}
                        </span>
                      ))}

                      {props.valueSuffix ? (
                        <span
                          className={clsx(option.description ? cssExports.withSubText : "")}
                        >{` ${props.valueSuffix}`}</span>
                      ) : (
                        ""
                      )}
                    </span>
                    {option.description && <span className={cssExports.subText}>{option.description}</span>}
                  </span>
                </Grid>
              </Grid>
            )}
          </li>
        );
      }}
      classes={{
        root: clsx(cssExports.autoCompleteRoot, cssExports.multipleAutoCompleteInput),
        popper: clsx(cssExports.autoCompletePopper, props.classes?.popper),
        paper: clsx(cssExports.autoCompleteContainer, cssExports.checkBoxOption),
        option: cssExports.autoCompleteOption,
        groupLabel: cssExports.autoCompleteGroupTitle,
        noOptions: cssExports.autoCompleteNoOptions,
        inputRoot: clsx(
          props.disabled || props.success || props.error || (props.showLoading && loading)
            ? cssExports.autoCompleteInputWithEndAdornment
            : "",
          cssExports.autoCompleteInputRoot
        ),
        endAdornment: cssExports.autoCompleteEndAdornment,
        clearIndicator: cssExports.autoCompleteClearIndicator,
        popperDisablePortal: cssExports.popperDisabledPortal,
        popupIndicator: cssExports.autoCompletePopUpIndicator,
      }}
      renderInput={(params) => (
        <span className={clsx(cssExports.iconInputContainer)}>
          <TextField
            {...params}
            name={props.name}
            placeholder={`${props.placeholder ?? props.label}${
              props.optional
                ? `${props.placeholder || props.label ? " " : ""}(${LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Common.Optional
                  )})`
                : ""
            }`}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              error: props.hasError,
              type: "search",
              onBlur: props.onBlur,
              onFocus: props.onFocus,
              classes: {
                root: cssExports.inputRoot,
                input: clsx(cssExports.inputInput, selectedItems?.length ? cssExports.hasValue : ""),
                focused: cssExports.focusedInput,
                error: cssExports.errorInput,
                disabled: cssExports.disabledInput,
                notchedOutline: cssExports.notchedOutline,
              },
              endAdornment: (
                <>
                  {props.valueLoading && (
                    <InputAdornment position="end" className={cssExports.inputAdornedEnd}>
                      <CircularProgress size={20} className={cssExports.inputProgress} />
                    </InputAdornment>
                  )}
                </>
              ),
            }}
            InputLabelProps={{
              shrink: false,
              classes: {
                root: clsx(cssExports.inputLabel),
                error: cssExports.errorLabel,
                shrink: cssExports.shrinkedLabel,
                focused: cssExports.focusedLabel,
              },
            }}
            onKeyDown={handleKeyDown}
          />
        </span>
      )}
      renderTags={(value: AutocompleteListItem[], getTagProps) => (
        <Grid item container className={clsx(props.className, cssExports.selectionChipsGroup)}>
          <Grid item container className={cssExports.selectionChips} spacing={1} direction="row">
            {value.map((item, index: number) => (
              // Keys include the index: chips with identical values share the same id, and duplicate
              // React keys corrupt reconciliation (removed chips can linger in the DOM as ghosts).
              <Grid item key={`${item.id}-${index}`} className={cssExports.chipContainer}>
                <Chip
                  {...getTagProps({ index })}
                  key={`${item.id}-${index}`}
                  className={clsx(cssExports.chip, cssExports.selected)}
                  variant={"outlined"}
                  data-key={item.id}
                  label={
                    <Grid container direction="row" wrap="nowrap">
                      <Grid item xs className={cssExports.chipLabel}>
                        {item.value}
                      </Grid>
                      {item.selectedSubText ? (
                        <Grid item>
                          <label className={cssExports.selectedSubText}>({item.selectedSubText})</label>
                        </Grid>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  }
                  onDelete={
                    props.disabled || item.locked
                      ? undefined
                      : () => {
                          handleRemoveAt(index);
                        }
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      renderGroup={(option) => (
        <li key={option.key}>
          {!UtilsHelper.isEmpty(option.key) ? (
            <Grid container direction="row" className={cssExports.autoCompleteGroupTitle}>
              <Grid item xs>
                {props.groupLabelFormatter ? props.groupLabelFormatter(option.key) : option.group}
              </Grid>
              {props.groupRightContent && props.groupRightContent(option.key)}
            </Grid>
          ) : (
            <></>
          )}
          <ul>{option.children}</ul>
        </li>
      )}
      groupBy={(option) => (option.group === undefined ? "" : `${option.group}`)}
      getOptionDisabled={(option) =>
        !!option.disabled || (currentAction !== undefined && currentAction?.id === option.id)
      }
      filterOptions={(options) =>
        options.filter(
          (option) => (!props.error || !option.hideWhenInvalid) && (!option.hide || !option.hide(options, inputValue))
        )
      }
      inputValue={inputValue}
      disableCloseOnSelect
      onOpen={onOpen}
      onClose={onClose}
      onChange={onChange}
      onInputChange={onInputChange}
      disabled={props.disabled}
      data-testid={`${props.name}MultiSearchInput`}
    />
  );
};

export default MultiSearchInput;
